import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  HeadingText,
  ParagraphText,
  UBRadioBox,
} from "../../../../Libraries";
import TabAutoComplete from "../../../../Libraries/TabAutoComplete";
import { AppSelectOptionList } from "../../../common";
import { RechargePlaceholderIcon } from "../../../../assets/icons/icon";
import AddCustomerModal from "../../../common/Modals/AddCustomerModal";
import PackageSection from "./PackageSection";
import { EasyloadAndPackages } from "../../../../constants";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { phoneNumRegex } from "../../../../validations";
import { useSnackbar } from "../../../../context/SnackbarContext";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import rechargeFormStyles from "./RechargeFormSection.module.css";
import { handleApi } from "../../../../apis";
import { SEARCH_RECHARGE_CUSTOMER } from "../../../../apis/configuration";

const
  RechargeFormSection = ({
    I18n,
    options,
    handleClick,
    state,
    setState,
    phoneOptions,
    handlePhoneTag,
    networkTypeValue,
    selectedLoad,
    setSelectedLoad,
    bundleTypes,
    bundles,
    setCustomer,
    customers = [],
    isDisabledEasyLoad,
    isDisabledPackage,
    onNext = () => { },
    isHiddenTelcoAndContact = false,
    isShowOnlyTelco = false,
    isPackageSelected = false,
    contactError = "",
  }) => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const [createCustomerModal, setCreateCustomerModal] = useState(false);
    const [initName, setInitname] = useState("");
    const [initPhone, setInitphone] = useState("");
    const [contactToUpdate, setContactToUpdate] = useState(null);
    const [isContactListOpen, setIsContactListOpen] = useState(false);
    const [rechargeCustomerSuggestions, setRechargeCustomerSuggestions] = useState([]);
    const [isSearchingRechargeCustomer, setIsSearchingRechargeCustomer] = useState(false);
    // Tracks logo URLs that failed to load, keyed per item, so those items
    // fall back to RechargePlaceholderIcon instead of a broken image.
    const [failedIcons, setFailedIcons] = useState({});
    const markIconFailed = (key) =>
      setFailedIcons((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

    const rechargeSearchDebounceRef = useRef(null);
    const rechargeSearchCancelRef = useRef(null);

    useEffect(() => {
      return () => {
        if (rechargeSearchDebounceRef.current) clearTimeout(rechargeSearchDebounceRef.current);
        if (rechargeSearchCancelRef.current) rechargeSearchCancelRef.current.cancel();
      };
    }, []);

    const searchRechargeCustomers = (typedValue) => {
      if (rechargeSearchDebounceRef.current) clearTimeout(rechargeSearchDebounceRef.current);
      if (rechargeSearchCancelRef.current) rechargeSearchCancelRef.current.cancel();

      const digits = (typedValue || "").replace(/[^0-9]/g, "");
      if (digits.length < 3) {
        setIsSearchingRechargeCustomer(false);
        setRechargeCustomerSuggestions([]);
        return;
      }

      rechargeSearchDebounceRef.current = setTimeout(async () => {
        setIsSearchingRechargeCustomer(true);
        rechargeSearchCancelRef.current = axios.CancelToken.source();
        try {
          const response = await handleApi(
            SEARCH_RECHARGE_CUSTOMER,
            { customer_phone: digits },
            rechargeSearchCancelRef.current,
            navigate,
          );
          setIsSearchingRechargeCustomer(false);
          setRechargeCustomerSuggestions(response?.customers?.length ? response.customers : []);
        } catch (err) {
          if (!axios.isCancel(err)) {
            setIsSearchingRechargeCustomer(false);
            setRechargeCustomerSuggestions([]);
          }
        }
      }, 300);
    };

    const handleRechargeCustomerSelect = (item) => {
      logContactSelect();
      const localMatch = (customers || []).find((c) => c.phone === item.customer_phone);
      setCustomer(localMatch);
      setState((e) => ({
        ...e,
        customerName: item.customer_name || "",
        customerNumber: item.customer_phone || "",
      }));
      setRechargeCustomerSuggestions([]);
    };

    const rechargeSuggestionList = useMemo(
      () =>
        rechargeCustomerSuggestions.map((item, index) => {
          const iconKey = `recharge_contact_${item.customer_phone || index}`;
          return {
            label: item.customer_name || item.customer_phone || "",
            value: item.customer_phone || "",
            subLabel: item.customer_phone || "",
            img:
              item.logo_link && !failedIcons[iconKey] ? (
                <img
                  src={item.logo_link}
                  alt={item.customer_name || ""}
                  width={28}
                  height={28}
                  style={{ objectFit: "contain" }}
                  onError={() => markIconFailed(iconKey)}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                  <RechargePlaceholderIcon width={19} height={19} />
                </Box>
              ),
            raw: item,
          };
        }),
      [rechargeCustomerSuggestions, failedIcons],
    );

    const contactList = useMemo(
      () =>
        (customers || []).map((item) => ({
          id: item.id,
          labelText: item.name,
          label: (
            <ParagraphText className={formStyles.contactLabel}>
              <span className="newFontColor">{item.name}</span>
              <span className="newLightColor">
                {item?.is_supplier ? I18n.supplier : I18n.customer}
              </span>
            </ParagraphText>
          ),
          value: item.name,
        })),
      [customers, I18n],
    );
    const openCreateCustomerModal = (value) => {
      if (value?.match?.(phoneNumRegex) === null) {
        setInitname(value);
        setInitphone("");
      } else {
        setInitphone(value);
        setInitname("");
      }
      setCreateCustomerModal(true);
    };

    const handleCreateContact = (name) => {
      openCreateCustomerModal(name);
    };

    const handleAddCustomer = (payload) => {
      setCreateCustomerModal(false);
      setContactToUpdate(null);
      const c = (customers || []).find((x) => x.id === payload?.id);
      setCustomer(c ?? payload);
      setState((e) => ({
        ...e,
        customerName: payload?.name || "",
        customerNumber: payload?.phone || "",
      }));
    };

    const logContactSelect = () => {
      triggerLogEvent(
        analytics,
        selectedLoad === EasyloadAndPackages.EASYLOAD
          ? "w_earn_ep_easyload_contact"
          : "w_earn_ep_packages_contact",
      );
    };

    const handlePhoneInputChange = (typedValue) => {
      // While the contacts list is open, typing searches by name only —
      // don't overwrite the selected/typed number until an item is picked.
      if (isContactListOpen) return;
      const match = (customers || []).find((c) => c.phone === typedValue);
      if (match) {
        logContactSelect();
        setCustomer(match);
        setState((e) => ({
          ...e,
          customerName: match.name || "",
          customerNumber: match.phone || "",
        }));
      } else {
        setCustomer(undefined);
        setState((e) => ({
          ...e,
          customerName: typedValue,
          customerNumber: typedValue,
        }));
      }
      searchRechargeCustomers(typedValue);
    };

    return (
      <Box className="accountModalBody">
        <ParagraphText className="fontSize14 blue800Color InputLabelStyle mb-2">
          {I18n.recharge_type}
        </ParagraphText>

        <Box className={rechargeFormStyles.radioBoxWrapper}>
          <UBRadioBox
            label={EasyloadAndPackages.EASYLOAD}
            displayLabel={I18n.easyload}
            key={EasyloadAndPackages.EASYLOAD}
            index={EasyloadAndPackages.EASYLOAD}
            value={selectedLoad}
            onChange={(value) => setSelectedLoad(value)}
            disabled={isDisabledEasyLoad}
          />
          <UBRadioBox
            label={EasyloadAndPackages.PACKAGES}
            displayLabel={I18n.packages}
            key={EasyloadAndPackages.PACKAGES}
            index={EasyloadAndPackages.PACKAGES}
            value={selectedLoad}
            onChange={(value) => setSelectedLoad(value)}
            disabled={isDisabledPackage}
          />
        </Box>

        {(!isHiddenTelcoAndContact || isShowOnlyTelco || isPackageSelected) && (
          <Fragment>
            <HeadingText className="fontSize14 fontWeight400 blue800Color">
              {I18n.choose_mobile_network}
            </HeadingText>

            <AppSelectOptionList
              data={options}
              handleClick={handleClick}
              value={state.platform}
              placeholderIcon={RechargePlaceholderIcon}
            />
          </Fragment>
        )}

        <Box mt={1} />

        {state?.platform ? (
          <Fragment>
            {selectedLoad === EasyloadAndPackages.EASYLOAD ? (
              (!isHiddenTelcoAndContact || isShowOnlyTelco) && (
                <Box
                  className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}
                >
                  <TabAutoComplete
                    id="easyload_contactlist"
                    list={isContactListOpen ? contactList : rechargeSuggestionList}
                    listHeader={isContactListOpen ? "" : I18n.results}
                    label={I18n.phone_number}
                    handleSelect={(item) => {
                      if (!item) {
                        setCustomer(undefined);
                        setState((e) => ({
                          ...e,
                          customerName: "",
                          customerNumber: "",
                        }));
                        return;
                      }
                      if (item.raw) {
                        handleRechargeCustomerSelect(item.raw);
                        return;
                      }
                      const c = (customers || []).find((x) => x.id === item.id);
                      if (c?.phone) {
                        logContactSelect();
                        setCustomer(c);
                        setState((e) => ({
                          ...e,
                          customerName: c?.name || item.labelText || "",
                          customerNumber: c?.phone || "",
                        }));
                      } else {
                        setContactToUpdate(c);
                        showSnackbar({ message: I18n.please_add_phone_number_to_continue, severity: "info" });
                        setCreateCustomerModal(true);
                      }
                    }}
                    value={state.customerNumber || ""}
                    handleCreate={handleCreateContact}
                    onInputChange={handlePhoneInputChange}
                    onOpenChange={(_isOpen, isContactsMode) => setIsContactListOpen(isContactsMode)}
                    manualTrigger
                    openOnType
                    loading={!isContactListOpen && isSearchingRechargeCustomer}
                    numericWhenClosed
                    suffixLabel={I18n.contacts}
                    placeholder={I18n.phone_number}
                    asCreate={I18n.customer}
                    required
                    error={contactError}
                    handleStartLogEvent={() =>
                      triggerLogEvent(analytics, "w_req_contact_start")
                    }
                    manuallyFocusInput={false}
                    handleManuallyInput={() => { }}
                  />
                </Box>
              )
            ) : (
              <Box className={rechargeFormStyles.packageInputWrapper}>
                <PackageSection
                  bundleTypes={bundleTypes}
                  bundles={bundles}
                  setState={setState}
                  selectedPackage={state?.offer}
                  onNext={onNext}
                />
              </Box>
            )}

            {selectedLoad === EasyloadAndPackages.EASYLOAD &&
              !isHiddenTelcoAndContact && (
                <Fragment>
                  <ParagraphText className="fontSize14 newFontColor InputLabelStyle mb-2">
                    {I18n.network_type}
                  </ParagraphText>

                  <Box className={rechargeFormStyles.radioBoxWrapper}>
                    {phoneOptions.length > 0 &&
                      phoneOptions.map((item) => (
                        <UBRadioBox
                          label={item.label}
                          displayLabel={I18n[item.label.toLowerCase()]}
                          key={item.label}
                          index={item.label}
                          value={networkTypeValue}
                          onChange={(value) => handlePhoneTag(value)}
                        />
                      ))}
                  </Box>
                </Fragment>
              )}
          </Fragment>
        ) : null}

        <AddCustomerModal
          isOpen={createCustomerModal}
          handleClose={() => {
            setCreateCustomerModal(false);
            setContactToUpdate(null);
          }}
          navigate={navigate}
          initialName={initName}
          initialPhone={initPhone}
          onSaveCallback={handleAddCustomer}
          type="in"
          title={
            <ParagraphText className={"ms-1 textLowercase"}>
              {I18n.customer}
            </ParagraphText>
          }
          isTypeField={true}
          isOnlyPhoneList={true}
          customerToUpdate={contactToUpdate}
          handleLogEventCustomerCreated={() =>
            triggerLogEvent(analytics, "w_req_customer_created")
          }
          handleLogEventCustomerUpdate={() =>
            triggerLogEvent(analytics, "customer_data_updated")
          }
        />
      </Box>
    );
  };

export default RechargeFormSection;
